/**
 * TGQuestionnaireRHF — контролируемый компонент опросника для React Hook Form
 *
 * Полностью интегрирован с RHF, поддерживает:
 * - Автоматическое восстановление ответов из внешнего state
 * - Режим read-only (disabled)
 * - Обратный вызов при любом изменении (onChange)
 * - Вычисление прогресса (progressPercent + answers в мета-поля формы)
 *
 * @example
 * <FormProvider {...form}>
 *   <TGQuestionnaireRHF
 *     questions={questions}
 *     answers={savedAnswers} // Восстанавливает состояние
 *     disabled={isReadOnly}
 *     onChange={(answers) => console.log('Updated:', answers)}
 *   />
 * </FormProvider>
 */
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import TextArea from "src/components/ui/form/textArea/textArea";
import { TGSelectPickerByList, TGSelectUserPickerByProperties, Option } from "src/components/ui/TGSelect";
import { Rates } from "../tables/notificationTables/GetNotificationTable";
import { useGetSelect } from "src/hooks/redux-selectors";
import {
  myTasksDepartmentsListName,
  myTasksLocationsListName,
  myTasksRegionsListName,
} from "src/api/config/myTasksLists.config";
import { TGCheckList } from "./TGCheckList";
import { CheckBoxListItem } from "src/types/main";

export type QuestionType =
  | "location"
  | "department"
  | "sector"
  | "employee"
  | "date"
  | "free"
  | "grade"
  | "many"
  | "single";

export type QuestionOption = {
  id: string;
  name: string;
  isCorrect?: boolean;
  withAction?: boolean;
};

export type QuestionItem = {
  name: string; // unique field key
  type: QuestionType;
  isRequired: boolean;
  options?: QuestionOption[];
  value?: { id: string; name: string; type: QuestionType };
  id?: string; // stable identifier for answers map (preferred over name)
};

type PerQuestionScore = Record<string, number>;

export type TGQuestionnaireRHFProps = {
  questions: QuestionItem[];
  answers?: Record<string, unknown>; // External answers to prefill
  disabled?: boolean; // Read-only mode
  onChange?: (answers: Record<string, unknown>) => void; // Callback on any change
  onProgressChange?: (percent: number, perQuestion: PerQuestionScore) => void;
  className?: string;
  hasAddedAction?: boolean;
};

// Build stable key for answers (prefer q.id, fallback to type+index)
function getAnswerKey(q: QuestionItem, index: number): string {
  const trimmed = (q.id || "").trim();
  return trimmed ? trimmed : `${q.type}_${index}`;
}

// Computes progress considering only questions of types: 'single', 'many', 'grade'.
// Each such question has equal weight. Scoring is strictly id-based.
export function computeProgress(
  qs: QuestionItem[],
  answers: Record<string, unknown>
): { percent: number; perQuestion: PerQuestionScore } {
  // Safe case-insensitive equality for names (avoids MicrosoftAjax recursion)
  const equalsCI = (a: unknown, b: unknown): boolean => {
    const sa = typeof a === "string" ? a.trim().toLowerCase() : "";
    const sb = typeof b === "string" ? b.trim().toLowerCase() : "";
    return !!sa && sa === sb;
  };

  // Read ids from enriched answer shapes
  const readSingleSelectedId = (raw: unknown): string => {
    if (typeof raw === "string") return raw;
    if (raw && typeof raw === "object") {
      const r = raw as any;
      return String(r?.value?.id || r?.rest?.value?.id || r?.id || "");
    }
    return "";
  };

  const readManySelectedIds = (raw: unknown): string[] => {
    if (!Array.isArray(raw)) return [];
    return (raw as any[])
      .map((item) => {
        if (typeof item === "string") return item;
        const r = item as any;
        return String(r?.value?.id || r?.rest?.value?.id || r?.id || "");
      })
      .filter((id) => typeof id === "string" && id.length > 0);
  };

  const perQuestionScores: PerQuestionScore = {};
  const countedTypes: QuestionType[] = ["single", "many", "grade"];

  const isQuestionNASelected = (q: QuestionItem, value: unknown): boolean => {
    const options = Array.isArray(q.options) ? q.options : [];
    const na = options.find((o) => equalsCI(o?.name, "Не применимо"));
    if (!na) return false;
    if (q.type === "single") {
      const selectedId: string = readSingleSelectedId(value);
      return !!selectedId && selectedId === na.id;
    }
    if (q.type === "many") {
      const selectedIds: string[] = readManySelectedIds(value);
      return selectedIds.includes(na.id);
    }
    return false;
  };

  // IMPORTANT: use original index in qs for keys to match how answers are re-keyed
  const effectiveQuestions: Array<{ q: QuestionItem; idx: number }> = [];
  qs.forEach((q, originalIdx) => {
    if (!countedTypes.includes(q.type)) return;
    const key = getAnswerKey(q, originalIdx);
    if (!isQuestionNASelected(q, answers[key])) {
      effectiveQuestions.push({ q, idx: originalIdx });
    }
  });

  if (effectiveQuestions.length === 0) {
    return { percent: 0, perQuestion: perQuestionScores };
  }

  const weightPerQuestion: number = 100 / effectiveQuestions.length;
  let totalPercent: number = 0;

  for (const { q: question, idx } of effectiveQuestions) {
    const key = getAnswerKey(question, idx);
    const rawValue = answers[key]; // тут underfined потому что index разные
    const options = Array.isArray(question.options) ? question.options : [];
    let scoreForQuestion = 0;

    if (question.type === "single") {
      const selectedId: string = readSingleSelectedId(rawValue);
      const correctIds: string[] = options.filter((opt) => !!opt.isCorrect).map((opt) => opt.id);
      if (selectedId && correctIds.includes(selectedId)) {
        scoreForQuestion = weightPerQuestion;
      }
    } else if (question.type === "many") {
      const selectedIds: string[] = readManySelectedIds(rawValue);
      const correctIds: string[] = options.filter((opt) => !!opt.isCorrect).map((opt) => opt.id);
      const numCorrectTotal = correctIds.length;

      if (numCorrectTotal > 0 && selectedIds.length > 0) {
        // Check if any selected answer is incorrect
        const hasIncorrectChoice = selectedIds.some((id) => !correctIds.includes(id));

        if (hasIncorrectChoice) {
          // If user selected at least one incorrect answer, score = 0
          scoreForQuestion = 0;
        } else {
          // If all selected answers are correct, award points proportionally
          const numPickedCorrect = selectedIds.filter((id) => correctIds.includes(id)).length;
          scoreForQuestion = weightPerQuestion * (numPickedCorrect / numCorrectTotal);
        }
      }
    } else if (question.type === "grade") {
      const g = typeof rawValue === "number" ? rawValue : Number(rawValue);
      const clamped = Number.isFinite(g) ? Math.min(5, Math.max(0, g)) : 0; // 0..5
      scoreForQuestion = weightPerQuestion * (clamped / 5);
    }

    const rounded = Number(scoreForQuestion.toFixed(1));
    perQuestionScores[key] = rounded;
    totalPercent += rounded;
  }

  return { percent: Number(totalPercent.toFixed(1)), perQuestion: perQuestionScores };
}

export function TGQuestionnaireRHF({
  questions,
  answers: externalAnswers,
  disabled = false,
  onChange,
  onProgressChange,
  className,
  hasAddedAction = true,
}: TGQuestionnaireRHFProps) {
  const { control, setValue, register, watch } = useFormContext();
  const [actionsByQuestion, setActionsByQuestion] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: locations } = useGetSelect(myTasksLocationsListName);
  const { data: departments } = useGetSelect(myTasksDepartmentsListName);
  const { data: sectors } = useGetSelect(myTasksRegionsListName);

  // Build map of question names to arrays of option IDs with withAction flag
  function updateActionsByQuestion(nextQuestions: QuestionItem[]) {
    const nextActionsByQuestion = nextQuestions.reduce((acc: string[], question) => {
      if (question.type === "single" || question.type === "many") {
        // Collect IDs of ALL options with withAction flag in one iteration
        if (question.options && Array.isArray(question.options)) {
          question.options.forEach((o) => {
            if (o.withAction) acc.push(o.id);
          });
        }
      }
      return acc;
    }, []);
    setActionsByQuestion(nextActionsByQuestion);
  }

  useEffect(() => {
    updateActionsByQuestion(questions);
  }, [questions]);

  const selectFieldBase = useMemo(
    () => ({
      width: "w-full",
      title: "Значение",
      require: false,
      type: "select",
      single: true,
    }),
    []
  );

  // Register meta fields once
  useEffect(() => {
    register("progressPercent");
    register("answers");
  }, [register]);

  // Prefill form fields from external answers (on mount or when answers/questions change)
  useEffect(() => {
    if (!externalAnswers || !questions.length) {
      setIsInitialized(true);
      return;
    }

    questions.forEach((q, idx) => {
      const stableKey = getAnswerKey(q, idx);
      const savedValue = externalAnswers[stableKey];

      if (savedValue === undefined) return;

      // Map saved value to correct field shape based on question type
      let fieldValue: any = savedValue;

      switch (q.type) {
        case "single": {
          // Expect { id, title, value? } or reconstruct from options
          if (savedValue && typeof savedValue === "object") {
            const raw = savedValue as any;
            const id = String(raw.id ?? "");
            let title = String(raw.title ?? raw.name ?? "");

            if (!title && q.options) {
              const found = q.options.find((opt) => String(opt.id) === id);
              if (found) {
                title = found.name;
                fieldValue = { id, title, value: found };
              }
            } else {
              fieldValue = { id, title, value: raw.value };
            }
          }
          break;
        }
        case "many": {
          // Array of { id, title, value? }
          if (Array.isArray(savedValue)) {
            fieldValue = savedValue.map((item: any) => {
              const id = String(item?.id ?? "");
              let title = String(item?.title ?? item?.name ?? "");

              if (!title && q.options) {
                const found = q.options.find((opt) => String(opt.id) === id);
                if (found) {
                  title = found.name;
                  return { id, title, value: found };
                }
              }
              return { id, title, value: item?.value };
            });
          }
          break;
        }
        // Other types can use value as-is (location, department, employee, date, free, grade)
      }

      setValue(q.name, fieldValue, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
    });

    // After prefilling, compute initial progress and set meta fields once
    try {
      const ext = externalAnswers as Record<string, unknown>;
      const { percent } = computeProgress(questions, ext);
      const rounded = Number(percent.toFixed(1));
      setValue("progressPercent", rounded, { shouldTouch: false, shouldValidate: false, shouldDirty: false });
      setValue("answers", ext, { shouldTouch: false, shouldValidate: false, shouldDirty: false });
    } catch (_e) {
      // ignore
    }

    setIsInitialized(true);
  }, [externalAnswers, questions, setValue]);

  // Subscribe to form changes and emit onChange + compute progress
  useEffect(() => {
    if (!isInitialized) return;

    const subscription = watch((values, info) => {
      // Avoid infinite loop when we update meta fields
      if (info?.name === "progressPercent" || info?.name === "answers") return;

      // Build stable-keyed answers map
      const vmap = values as Record<string, unknown>;
      const rekeyed: Record<string, unknown> = {};
      questions.forEach((q, idx) => {
        const key = getAnswerKey(q, idx);
        rekeyed[key] = vmap[q.name as any];
      });

      // Compute progress
      const { percent, perQuestion } = computeProgress(questions, rekeyed);
      const rounded = Number(percent.toFixed(1));

      // Update meta fields
      setValue("progressPercent", rounded, { shouldTouch: false, shouldValidate: false, shouldDirty: false });
      setValue("answers", rekeyed, { shouldTouch: false, shouldValidate: false, shouldDirty: false });

      // Emit callbacks
      if (onChange) onChange(rekeyed);
      if (onProgressChange) onProgressChange(rounded, perQuestion);
    });

    return () => subscription.unsubscribe();
  }, [watch, questions, setValue, onChange, onProgressChange, isInitialized]);

  return (
    <FlexCol className={twMerge("w-full custom-form", className)}>
      {questions.map((q) => {
        const key = q.name;
        if (q.type === "location" || q.type === "department" || q.type === "sector") {
          const opts = q.type === "location" ? locations : q.type === "department" ? departments : sectors;

          return (
            <Controller
              key={key}
              name={key as any}
              control={control}
              rules={{ required: q.isRequired }}
              render={({ field, fieldState }) => (
                <TGSelectPickerByList<Option>
                  list={opts.map((o) => ({ id: o.ID.toString(), name: o.Title }))}
                  values={Array.isArray(field.value) ? (field.value as Option[]) : []}
                  onChange={(v) => field.onChange(v)}
                  fetchKey="static"
                  errors={{ [key]: fieldState.error }}
                  dataField={{
                    ...selectFieldBase,
                    title: q.name,
                    key,
                    require: q.isRequired,
                    disabled: disabled,
                    hideRemoveButton: true,
                  }}
                  classNames={{ labelClassName: "text-sm font-medium text-black" }}
                />
              )}
            />
          );
        }

        if (q.type === "employee") {
          return (
            <Controller
              key={key}
              name={key as any}
              control={control}
              rules={{ required: q.isRequired }}
              render={({ field, fieldState }) => (
                <TGSelectUserPickerByProperties
                  values={Array.isArray(field.value) ? (field.value as Option[]) : []}
                  onChange={(v) => field.onChange(v)}
                  fetchKey="users"
                  errors={{ [key]: fieldState.error }}
                  dataField={{
                    ...selectFieldBase,
                    title: q.name,
                    key,
                    require: q.isRequired,
                    disabled: disabled,
                  }}
                  classNames={{ labelClassName: "text-sm font-medium text-black" }}
                />
              )}
            />
          );
        }

        if (q.type === "date") {
          return (
            <Controller
              key={key}
              name={key as any}
              control={control}
              rules={{ required: q.isRequired }}
              render={({ field, fieldState }) => {
                const value = field.value as string | undefined; // ISO string
                const dateValue: DateValueType = value
                  ? { startDate: new Date(value), endDate: new Date(value) }
                  : { startDate: null, endDate: null };
                return (
                  <div className="mb-5 w-full">
                    <label className="flex justify-between text-sm font-medium text-black mb-2.5">
                      <span>{q.name}</span>
                      {q.isRequired && <span className="text-[#BA0C2F]">*</span>}
                    </label>
                    <Datepicker
                      value={dateValue}
                      asSingle={true}
                      useRange={false}
                      displayFormat="DD.MM.YYYY"
                      startWeekOn="mon"
                      inputClassName={(cls) => twMerge(cls, "text-black")}
                      i18n="ru"
                      onChange={(d) => {
                        const iso = d?.startDate ? new Date(d.startDate).toISOString() : "";
                        field.onChange(iso);
                      }}
                      disabled={disabled}
                    />
                    {fieldState.error && <div className="text-[#BA0C2F] text-sm mt-1">Заполните поле</div>}
                  </div>
                );
              }}
            />
          );
        }

        if (q.type === "free") {
          return (
            <Controller
              key={key}
              name={key as any}
              control={control}
              rules={{ required: q.isRequired }}
              render={({ field, fieldState }) => (
                <TextArea
                  value={(field.value as string) || ""}
                  onChange={(v) => field.onChange(v)}
                  errors={{ [key]: fieldState.error }}
                  dataField={{
                    width: "w-full",
                    title: q.name,
                    require: q.isRequired,
                    key,
                    type: "text",
                    placeholder: "",
                    disabled: disabled,
                  }}
                />
              )}
            />
          );
        }

        if (q.type === "grade") {
          return (
            <Controller
              key={key}
              name={key as any}
              control={control}
              rules={{ required: q.isRequired }}
              render={({ field }) => (
                <Rates
                  className="mb-5"
                  title={q.name}
                  initial={(field.value as number) || 0}
                  getResult={(v) => field.onChange(v)}
                  read={disabled}
                  question={{
                    QuestionTitle: q.name,
                    ID: 0,
                    Title: q.name,
                    QuestionType: "rate",
                    TemplateOWD: [],
                    Options: [],
                    Answers: [],
                    QuestionText: q.name,
                  }}
                />
              )}
            />
          );
        }

        if (q.type === "single") {
          const opts = q.options || [];
          return (
            <Controller
              key={key}
              name={key as any}
              control={control}
              rules={{ required: q.isRequired }}
              render={({ field, fieldState }) => {
                const selected = field.value as CheckBoxListItem;
                return (
                  <div className="mb-5 w-full">
                    <FlexCol className="gap-2">
                      <TGSelectPickerByList<Option>
                        key={`${key}-select`}
                        list={opts.map((o) => ({ id: o.id, name: o.name, value: o }))}
                        values={selected ? ([{ id: selected.id.toString(), name: selected.title }] as Option[]) : []}
                        onChange={(v) => {
                          const choice = v && v.length > 0 ? v[0] : undefined;
                          if (choice) {
                            field.onChange({ id: String(choice.id), title: String(choice.name), value: choice.value });
                          } else {
                            field.onChange(undefined);
                          }
                          updateActionsByQuestion(questions);
                        }}
                        fetchKey="static"
                        errors={{ [key]: fieldState.error }}
                        dataField={{
                          ...selectFieldBase,
                          title: q.name,
                          key,
                          require: q.isRequired,
                          single: true,
                          disabled: disabled,
                          hideRemoveButton: true,
                        }}
                        searchPlaceholder="Выберите вариант"
                        clear={false}
                        classNames={{ labelClassName: "text-sm font-medium text-black" }}
                      />
                    </FlexCol>
                    {fieldState.error && <div className="text-[#BA0C2F] text-sm mt-1">Выберите вариант</div>}
                    {hasAddedAction && selected?.id && actionsByQuestion.includes(selected.id.toString()) && (
                      <div className="w-full rounded-md bg-[#FEF3C7] border border-[#F2A900] p-2.5 mb-2.5 text-center">
                        Требуется создать действие
                      </div>
                    )}
                  </div>
                );
              }}
            />
          );
        }

        if (q.type === "many") {
          const opts = q.options || [];
          return (
            <Controller
              key={key}
              name={key as any}
              control={control}
              rules={{ required: q.isRequired }}
              render={({ field, fieldState }) => {
                const selected: CheckBoxListItem[] = Array.isArray(field.value)
                  ? (field.value as CheckBoxListItem[])
                  : [];

                const toggle = (item: CheckBoxListItem) => {
                  updateActionsByQuestion(questions);
                  if (selected.some((s) => s.id === item.id)) field.onChange(selected.filter((s) => s.id !== item.id));
                  else field.onChange([...selected, item]);
                };

                return (
                  <div className="mb-5 w-full">
                    <label className="flex justify-between text-sm font-medium text-black mb-2.5">
                      <span>{q.name}</span>
                      {q.isRequired && <span className="text-[#BA0C2F]">*</span>}
                    </label>
                    <FlexCol className="gap-2">
                      <TGCheckList
                        items={selected}
                        onChecked={(item) => toggle(item)}
                        readOnly={disabled}
                        list={opts.map((o) => ({ id: o.id, title: o.name, value: o }))}
                      />
                    </FlexCol>
                    {fieldState.error && <div className="text-[#BA0C2F] text-sm mt-1">{fieldState.error.message}</div>}
                    {hasAddedAction && selected.some((s) => actionsByQuestion.includes(s.id.toString())) && (
                      <div className="w-full rounded-md bg-[#FEF3C7] border border-[#F2A900] p-2.5 mb-2.5 mt-5 text-center">
                        Требуется создать действие
                      </div>
                    )}
                  </div>
                );
              }}
            />
          );
        }

        // Fallback: unknown type
        return (
          <FlexRow key={key} className="text-[#BA0C2F]">
            Неизвестный тип поля: {q.type}
          </FlexRow>
        );
      })}
    </FlexCol>
  );
}

export default TGQuestionnaireRHF;
